import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material";
import DropDownValue from "../baseComponents/dropdownvalue";
import { commaSeparatedPincodePattern, textWithoutSpacePattern } from "../../constants/patterns";
import modalStyle from "../../styles/modalStyle";
import Client from "../../interfaces/client";
import Picklist from "../../interfaces/picklist";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useAppDispatch } from "../../store/hooks";
import { decodedJWT, getOwnerName } from "../../utils/commonUtils";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import Segment from "../../interfaces/segment";
import CloseIcon from '@mui/icons-material/Close';
import { modalTypes } from "../manageSegment/manageSegment";
import apiClient from "../../axiosClient";
import { loadSegments } from "../../store/slices/segmentSlice";
import District from "../../interfaces/districts";

interface HandleSegmentModalProps {
    modalVisible: boolean;
    closeModal: () => void;
    modalType: string;
    selectedRow: Segment;
    setSelectedRow: React.Dispatch<React.SetStateAction<Segment[]>>;
};

const HandleSegmentModal: React.FC<HandleSegmentModalProps> = ({ modalVisible, closeModal, modalType, selectedRow, setSelectedRow }) => {
    const dispatch: AppDispatch = useAppDispatch();
    const token: JWTTokenPayload | false = decodedJWT();
    const ownerName = getOwnerName(token);
    const segments: Segment[] = useSelector((state: RootState) => state.segments.segments);
    const clients: Client[] = useSelector((state: RootState) => state.clients.clients);
    const picklists: Picklist = useSelector((state: RootState) => state.picklists.picklists);
    const districtStore = useSelector((state: RootState) => state.districtsAndStates);
    const districtList: District[] = districtStore.districts;
    const stateList: string[] = districtStore.states;
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [clientName, setClientName] = useState<string>('');
    const [dpdBuckets, setDpdBuckets] = useState<string[]>([]);
    const [pinCodes, setPinCodes] = useState<string>('');
    const [loanTypes, setLoanTypes] = useState<string[]>([]);
    const [states, setStates] = useState<string[]>([]);
    const [districts, setDistricts] = useState<string[]>([]);
    const [filteredDistricts, setFilteredDistricts] = useState<string[]>(districtList.map(val => val.district));
    const verticalStyle = { mb: 1, mt: 1 };

    useEffect(() => {
        // If edit option is selected, populate the values on modal open
        if (modalVisible && modalType === modalTypes.SEGMENT_EDIT) {
            setName(selectedRow.name);
            setDescription(selectedRow.description);
            setClientName(selectedRow.client_name);
            if (selectedRow.dpd_bucket) {
                setDpdBuckets(selectedRow.dpd_bucket);
            }
            if (selectedRow.loan_type) {
                setLoanTypes(selectedRow.loan_type);
            }
            if (selectedRow.pin_code) {
                setPinCodes(selectedRow.pin_code.join(','));
            }
            if (selectedRow.state) {
                setStates(selectedRow.state);
            }
            if (selectedRow.district) {
                setDistricts(districtList.filter(val => selectedRow.district.includes(val.district)).map(val => val.district));
            }
        }
    }, [modalVisible]);

    useEffect(() => {
        if (states.length === 0) {
            setFilteredDistricts(districtList.map(val => val.district));
        } else {
            setFilteredDistricts(districtList.filter(val => states.includes(val.state)).map(val => val.district));
            setDistricts(districtList.filter(val => states.includes(val.state) && districts.includes(val.district)).map(val => val.district));
        }
    }, [states]);

    const handleCloseModal = () => {
        setName('');
        setDescription('');
        setClientName('');
        setDpdBuckets([]);
        setLoanTypes([]);
        setPinCodes('');
        setStates([]);
        setDistricts([]);
        closeModal();
    };

    const handleSubmit = async () => {
        try {
            if (!name) {
                alert('Please type a name');
                return;
            }
            if (!description) {
                alert('Please name a description');
                return;
            }
            if (!clientName) {
                alert('Please select a client');
                return;
            }
            if (dpdBuckets.length === 0 && loanTypes.length === 0 && pinCodes.length === 0 && states.length === 0 && districts.length === 0) {
                alert('Please select DPD buckets, Loan Types, States, Districts, or type Pin codes to set segment filters');
                return;
            }
            if (!commaSeparatedPincodePattern.test(pinCodes) && pinCodes.length > 0) {
                alert('Please type pin codes in comma separated format');
                return;
            }
            let body: { [key: string]: any } = {
                agency_name: ownerName,
                name,
                description,
                is_active: true,
                client_name: clientName,
            };
            if (dpdBuckets.length > 0) {
                body.dpd_bucket = dpdBuckets;
            }
            if (loanTypes.length > 0) {
                body.loan_type = loanTypes;
            }
            if (pinCodes.length > 0) {
                body.pin_code = pinCodes.split(',');
            } else if (districts.length > 0) {
                body.district = districts;
            } else if (states.length > 0) {
                body.state = states;
            }
            let response = null;
            if (modalType === modalTypes.SEGMENT_CREATE) {
                response = await apiClient.post('/api/v1/segment', body);
            } else {
                response = await apiClient.put(`/api/v1/segment/${selectedRow.segment_id}`, body);
            }
            if (response.status === 200) {
                let newSegments: Segment[] = [];
                if (modalType === modalTypes.SEGMENT_CREATE) {
                    newSegments = [...segments, response.data as Segment];
                } else {
                    const segmentIndex = segments.findIndex(val => val.segment_id === selectedRow.segment_id);
                    if (segmentIndex !== -1) {
                        newSegments = [...segments.slice(0, segmentIndex), response.data as Segment, ...segments.slice(segmentIndex + 1)];
                        setSelectedRow([response.data as Segment]);
                    } else {
                        newSegments = segments;
                    }
                }
                dispatch(loadSegments(newSegments));
                handleCloseModal();
                alert('Success! Segment '+ (modalType === modalTypes.SEGMENT_CREATE ? 'Created' : 'Updated') +' successfully');
            } else {
                alert('Failure! Could not '+ (modalType === modalTypes.SEGMENT_CREATE ? 'create' : 'update') +' segment');
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    return (
        <Modal open={modalVisible} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                    {modalType === modalTypes.SEGMENT_CREATE ? 'Add New' : 'Edit'} Segment
                    </Typography>
                    <IconButton onClick={handleCloseModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <TextField
                    label="Segment Name"
                    name="segment name"
                    value={name}
                    onChange={(event) => setName(event?.target.value)}
                    fullWidth
                    required
                    sx={verticalStyle}
                />
                <TextField
                    label="Description"
                    name="description"
                    value={description}
                    onChange={(event) => setDescription(event?.target.value)}
                    fullWidth
                    required
                    sx={verticalStyle}
                />
                <DropDownValue
                    label="Select Client"
                    items={clients}
                    selectedValue={clientName}
                    onSelection={val => setClientName(val)}
                    keyProp="client_name"
                    displayProp="name"
                    valueProp="client_name"
                    required
                    style={verticalStyle}
                />
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: 2 }}>
                    <DropDownValue
                        label="Select DPD Buckets"
                        items={picklists.DPD_BUCKET}
                        selectedValue={dpdBuckets}
                        onSelection={val => setDpdBuckets(val)}
                        required
                        multiple
                        style={{ width: '49%' }}
                    />
                    <DropDownValue
                        label="Select Loan Types"
                        items={picklists.LOAN_TYPE}
                        selectedValue={loanTypes}
                        onSelection={val => setLoanTypes(val)}
                        required
                        multiple
                        style={{ width: '49%' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: 2 }}>
                    <DropDownValue
                        label="Select States"
                        items={stateList}
                        selectedValue={states}
                        onSelection={val => setStates(val)}
                        disabled={districts.length > 0 || pinCodes.length > 0}
                        required
                        multiple
                        style={{ width: '33%' }}
                    />
                    <DropDownValue
                        label="Select Districts"
                        items={filteredDistricts}
                        selectedValue={districts}
                        onSelection={val => setDistricts(val)}
                        disabled={pinCodes.length > 0}
                        required
                        multiple
                        style={{ width: '33%' }}
                    />
                    <TextField
                        label="Select Pincodes"
                        name="pincodes"
                        value={pinCodes}
                        onChange={(event) => setPinCodes(event?.target.value)}
                        disabled={districts.length > 0}
                        fullWidth
                        required
                        sx={{ width: '33%' }}
                    />
                </Box>
                <Box mt={2} textAlign="right">
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default HandleSegmentModal;